/**
 * Inline tool call display within chat messages.
 * Shows a compact badge with tool name, status, and spinner.
 * For pending_approval status, shows approve/deny action buttons.
 * Click to open the detail dialog.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check, X, ChevronRight, ShieldCheck } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { DisclosureChevron } from '../common/DisclosureChevron';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { getModuleById } from '../../config/modules.config';
import { useApproval, useApprovalActions, useModuleName } from '../../hooks';
import { ApprovalFidelityDetails } from '../approvals/ApprovalFidelityDetails';
import { isToolCallOutputError, type ToolCallData, type ToolEvent } from './types';

interface ToolCallBlockProps {
  /** Persisted tool call data (from DB) */
  toolCall?: ToolCallData;
  /** Live streaming tool event (during generation) */
  toolEvent?: ToolEvent;
  /** Callback to open tool call detail dialog (state lifted to parent) */
  onOpenDetail?: (toolCallId: string) => void;
  /** Optional: collapse the inline pending banner immediately on resolve start. */
  onResolveStart?: (toolCallId: string, action: 'approve' | 'reject' | 'always_approve') => void;
}

export const ToolCallBlock: React.FC<ToolCallBlockProps> = ({
  toolCall,
  toolEvent,
  onOpenDetail,
  onResolveStart,
}) => {
  const { t } = useTranslation(['common', 'aiChat']);
  const getModuleName = useModuleName();

  // Normalize data from either source
  const toolName = toolCall?.tool_name || toolEvent?.toolName || 'Unknown tool';
  const module = toolCall?.module || toolEvent?.module || '';
  const rawStatus = toolCall?.status || toolEvent?.status || 'running';
  const moduleConfig = getModuleById(module);
  const moduleLabel = moduleConfig ? getModuleName(moduleConfig) : module.replace(/_/g, ' ');
  const toolCallId = toolCall?.id || toolEvent?.toolCallId;
  const approvalId = toolCall?.approval_id || toolEvent?.approvalId;

  // Check for MCP isError in output even if backend reported success
  const status = (() => {
    if (rawStatus === 'pending_approval') return 'pending_approval' as const;
    if (toolCall && isToolCallOutputError(toolCall)) return 'error' as const;
    return rawStatus;
  })();

  const isPendingApproval = status === 'pending_approval' && !!approvalId;

  // Only fetch the approval when needed for the pending UI
  const { data: approval } = useApproval(isPendingApproval ? approvalId : undefined);
  const { resolve, resolving, tooltips, alwaysApproveNode } = useApprovalActions(approval, {
    onResolveStart: (action) => {
      if (toolCallId && action !== 'edit') onResolveStart?.(toolCallId, action);
    },
  });

  // Format the tool name for display:
  // 1. Remove module prefix (module__toolName)
  // 2. Strip _api_v1_... suffix from auto-generated names
  // 3. Humanize underscores
  const rawName = toolName.includes('__')
    ? toolName.split('__').slice(1).join('__')
    : toolName;
  const apiIdx = rawName.indexOf('_api_v1_');
  const cleanName = apiIdx > 0 ? rawName.substring(0, apiIdx) : rawName;
  const displayName = cleanName.replace(/_/g, ' ');

  const hasDetails = !!toolCall?.input_data || !!toolCall?.output_data || !!toolCallId;
  const isLoading = resolving !== null;
  const [showDetails, setShowDetails] = React.useState(false);

  // The compact inline badge + (for a pending approval) its action buttons.
  // A fragment so each return path wraps it in its own flex row — inline in
  // message text normally, or the top row of the pending-approval block.
  const inlineRow = (
    <>
      <div
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px]',
          'transition-colors duration-200',
          status === 'running' && 'border-info/40 bg-info/10 text-info',
          status === 'success' && 'border-success/40 bg-success/10 text-success',
          status === 'error' && 'border-destructive/40 bg-destructive/10 text-destructive',
          status === 'pending_approval' && 'border-warning/40 bg-warning/10 text-warning',
          hasDetails && !isPendingApproval && 'cursor-pointer hover:opacity-80',
        )}
        onClick={() => !isPendingApproval && hasDetails && toolCallId && onOpenDetail?.(toolCallId)}
        title={hasDetails ? `${moduleLabel}: ${displayName}` : displayName}
      >
        {status === 'running' && <Loader2 className="h-3 w-3 animate-spin shrink-0" />}
        {status === 'success' && <Check className="h-3 w-3 shrink-0" />}
        {status === 'error' && <X className="h-3 w-3 shrink-0" />}
        {status === 'pending_approval' && <ShieldCheck className="h-3 w-3 shrink-0" />}

        <span className="font-medium truncate max-w-[150px]">{displayName}</span>

        {!isPendingApproval && hasDetails && <ChevronRight className="h-3 w-3 opacity-50 shrink-0 rtl:rotate-180" />}
      </div>

      {isPendingApproval && (
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            aria-expanded={showDetails}
            title={t('aiChat:toolCall.reviewDetails', 'Review details')}
            className={cn(
              'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border text-[11px] font-medium',
              'transition-colors duration-200',
              'border-warning/40 bg-background text-warning',
              'hover:bg-warning/10',
            )}
          >
            <DisclosureChevron open={showDetails} className="h-3 w-3" />
            {t('aiChat:toolCall.review', 'Review')}
          </button>
          <button
            type="button"
            onClick={() => resolve('reject')}
            disabled={isLoading}
            title={tooltips.reject}
            className={cn(
              'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border text-[11px] font-medium',
              'transition-colors duration-200',
              'border-destructive/30 bg-background text-destructive',
              'hover:bg-destructive hover:text-destructive-foreground hover:border-destructive',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {resolving === 'reject' ? (
              <Loader2 className="h-3 w-3 animate-spin shrink-0" />
            ) : (
              <X className="h-3 w-3 shrink-0" />
            )}
            {t('common:app.reject')}
          </button>
          <button
            type="button"
            onClick={() => resolve('approve')}
            disabled={isLoading}
            title={tooltips.approve}
            className={cn(
              'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-medium',
              'transition-colors duration-200',
              'bg-success text-success-foreground hover:bg-success/90',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {resolving === 'approve' ? (
              <Loader2 className="h-3 w-3 animate-spin shrink-0" />
            ) : (
              <Check className="h-3 w-3 shrink-0" />
            )}
            {t('common:app.approve')}
          </button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => resolve('always_approve')}
                disabled={isLoading}
                className={cn(
                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-medium',
                  'transition-colors duration-200',
                  'bg-info text-info-foreground hover:bg-info/90',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                {resolving === 'always_approve' ? (
                  <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                ) : (
                  <Check className="h-3 w-3 shrink-0" />
                )}
                {t('common:app.alwaysApprove')}
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{alwaysApproveNode}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </>
  );

  // Non-approval tool calls stay inline within the message text.
  if (!isPendingApproval) {
    return <div className="inline-flex items-center gap-1 flex-wrap my-0.5">{inlineRow}</div>;
  }

  // A pending approval becomes a small block: the inline row plus an
  // expandable fidelity panel (raw operation + decision log + risk).
  return (
    <div className="my-0.5">
      <div className="inline-flex items-center gap-1 flex-wrap">{inlineRow}</div>
      {showDetails && approval && (
        <div className="mt-1 max-w-xl rounded-md border border-warning/40 bg-warning/10 p-2">
          <ApprovalFidelityDetails approval={approval} compact />
        </div>
      )}
    </div>
  );
};
